import { FC, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { Icon, IconSize } from '../Icon/Icon';
import { ButtonSize, ButtonVariation, DropdownButtonButton } from './DropdownButtonButton';

const StyledDropdownButton = styled.div`
  margin: 0;
  padding: 0;
  display: inline-flex;
  position: relative;
`;

const StyledLabel = styled.span`
  margin-right: 20px;
`;

const StyledMenuList = styled.ul<ICommonProps>`
  min-width: ${({ menuMinWidth }) => (menuMinWidth ? menuMinWidth + 'px' : 'auto')};
  margin: 0;
  position: absolute;
  z-index: 1;
  top: 110%;
  ${({ menuPlacement }) => menuPlacement === 'right' && 'right: 0;'}
  background-color: #fff;
  border: 1px solid ${theme.vars.newColor.lightGS1};
  white-space: nowrap;
`;

export type IMenuPlacement = 'left' | 'right';

interface ICommonProps {
  /** Minimum width of menu */
  menuMinWidth?: number;
  /** Placement of the dropdown menu. "left" (default) | "right" */
  menuPlacement?: IMenuPlacement;
}

export interface IProps extends ICommonProps {
  /** "normal" (default) | "primary" | "secondary" | "link" */
  variation?: ButtonVariation;
  /** Label of the dropdown */
  label?: string;
  /** Specify an icon to show icon instead of label */
  icon?: string;
  /** The size of the icon.  One of 'extra-small', 'small', 'normal' (default), large' or 'extra-large' */
  iconSize?: IconSize;
  /** Button size. One of "small", "medium-small", "medium" (default) or "large" */
  buttonSize?: ButtonSize;
  /** The icon color */
  iconColor?: string;
  /** Defaults to false.  When true, button appears "grayed out" and cannot be clicked */
  disabled?: boolean;
  /** Dropdown Button ARIA label tag */
  ariaLabel?: string;
}

export const DropdownButton: FC<IProps> = props => {
  const [isOpen, setIsOpen] = useState(false);
  let timeOutId = null;

  const onClickHandler = () => {
    setIsOpen(!isOpen);
  };

  // We close the popover on the next tick by using setTimeout.
  // This is necessary because we need to first check if
  // another child of the element has received focus as
  // the blur event fires prior to the new focus event.
  const onBlurHandler = () => {
    timeOutId = window.setTimeout(() => {
      setIsOpen(false);
    });
  };

  // If a child receives focus, do not close the popover.
  const onFocusHandler = () => {
    clearTimeout(timeOutId);
  };
  return (
    <StyledDropdownButton onBlur={onBlurHandler} onFocus={onFocusHandler} data-testid="DropdownButton">
      <DropdownButtonButton
        variation={props.variation}
        onClick={onClickHandler}
        disabled={props.children ? props.disabled : true}
        ariaLabel={props.ariaLabel}
        size={props.buttonSize}
        aria-haspopup="true"
        data-testid="DropdownButtonElement"
      >
        {props.label && <StyledLabel>{props.label}</StyledLabel>}
        <Icon
          icon={props.icon || 'chevron-down'}
          size={props.iconSize}
          color={props.iconColor || theme.vars.newColor.darkGS2}
        />
      </DropdownButtonButton>
      {isOpen && (
        <StyledMenuList onClick={onClickHandler} menuMinWidth={props.menuMinWidth} menuPlacement={props.menuPlacement}>
          {props.children}
        </StyledMenuList>
      )}
    </StyledDropdownButton>
  );
};
